<template>
  <div id="travel-policy">
    <b-card bg-variant="light" v-if="isLoaded">
      <b-card-header class="row title" v-b-toggle.def>
        <span class="header-title">{{ $t('travelPolicy.definition.title') }}</span>
        <span class="header-chevron"><i class="fas fa-chevron-down"></i></span>
      </b-card-header>

      <b-collapse id="def" visible>
        <b-card-body>
          <div class="row">
            <div class="col-6">
            <h6>{{ $t('travelPolicy.definition.mode') }}</h6>
            <radio-input name="mode" :options="mode.radioOptions"
                         v-model="mode.value" :required="false"
            />
            </div>

            <div class="col-6">
              <h6>{{ $t('travelPolicy.definition.prices') }}</h6>

                <number-input v-for="price in priceLabels" :key="price.key" class=""
                              v-model="defaultPrices[price.key]" :label="`travelPolicy.price.${price.key}`"
                              :required="false" is-price inline-display :name="price.key"
                              no-zero
                />

            </div>
          </div>
        </b-card-body>

        <b-card-footer>
          <div class="col-10">
            <b-alert dismissible :show="alert.def.show" :variant="alert.def.variant">
              {{ $t(`travelPolicy.alert.def.${alert.def.variant}`) }}
            </b-alert>
          </div>

          <div class="col-2">
            <button class="btn orange-white" @click="onSubmitTravelPolicy" :disabled="defaultSubmitDisabled">
              {{ $t('form.submit') }}
            </button>
          </div>
        </b-card-footer>
      </b-collapse>
    </b-card>

    <b-card bg-variant="light" v-show="addRowEnabled" class="add-travel-policy">
      <b-card-header class="row title" v-b-toggle.add>
        <span class="header-title">{{ $t('travelPolicy.add') }}</span>
        <span class="header-chevron"><i class="fas fa-chevron-down"></i></span>
      </b-card-header>

      <b-collapse id="add">
        <b-card-body>
          <div class="row">
            <div class="col-6">
              <radio-input name="add" :options="add.radioOptions"
                           v-model="add.values.type" :required="false"
              />

              <multiselect v-model="add.values.sector" :options="addOptions" label="text"
                           track-by="id" :placeholder="$t(addSelectPlaceholder)"
                           :searchable="true" :is-fetching="isFetching" :clear-on-select="true"
                           :close-on-select="true" @search-change="searchCity"
              >
                <template slot="option" slot-scope="{ option }">
                  <span class="option__desc">
                    <div class="option__title">
                      {{ option.text }}
                    </div>
                  </span>
                </template>

                <template slot="noResult">
                  {{ $t('_no_result_found') }}
                </template>

                <template slot="noOptions">
                  {{ $t('_no_result_found') }}
                </template>
              </multiselect>

              <h6 class="title-dates">{{ $t('travelPolicy.dates.title') }}</h6>
              <text-input v-for="date in datesLabels" :key="date.key"
                          v-model="add.values.dates[date.key]" :label="`travelPolicy.dates.${date.key}`"
                          :required="false" :placeholder="`travelPolicy.dates.format`" :name="date.key"
                          v-mask="'##/##'"
              />
          </div>

          <div class="col-6">
            <number-input v-for="price in priceLabels" :key="price.key"
                          v-model="add.values.prices[price.key]" :label="`travelPolicy.price.${price.key}`"
                          :required="false" is-price inline-display :name="price.key"
                          no-zero :ref="price.key"
            />
          </div>
        </div>
        </b-card-body>

        <b-card-footer>
          <div class="col-10 errors" v-if="errors.length !== 0 ">
            <b-alert  v-for="error in errors" v-bind:key="error" dismissible :show="alert.add.show" :variant="alert.add.variant">
              {{ $t(`error.${error.code}`) }}
            </b-alert>
          </div>

          <div class="col-10" v-else>
            <b-alert dismissible :show="alert.add.show" :variant="alert.add.variant">
              {{ $t(`travelPolicy.alert.add.${alert.add.variant}`) }}
            </b-alert>
          </div>

          <div class="col-2">
            <button class="btn orange-white" @click="onAdd" :disabled="addSubmitDisabled">
              {{ $t('_add') }}
            </button>
          </div>
        </b-card-footer>
      </b-collapse>
    </b-card>

    <div class="col-12"><h6 class="policy-selected-title bg-light rounded">{{ $t('travelPolicy.selected') }}</h6></div>
    <b-card v-for="row in rows" class="travel-policy-selected">
      <b-card-header class="row">
        <div class="col-11">
          <span>[{{ $t(`sector.type.${row.type}`) }}]</span>
          <span> {{ row.sector.text }}</span>
          <span v-if="row.sector.def"> - {{ row.sector.def }}</span>
        </div>

        <div class="col-1">
          <button class="btn orange-white" :title="$t('_delete')" @click="onDelete(row.id)">
            <fa fa-class="trash-alt" />
          </button>
        </div>
      </b-card-header>

      <b-card-text>
        <div class="row">
          <span v-for="(date, key) in row.dates" v-bind:key="key" class="card-text-detail">
            {{ dateTrans(key) }} : {{ date }}
          </span>
        </div>
        <div class="row">
          <span v-for="(price, key) in row.prices" class="card-text-detail">
            {{ priceTrans(key) }} : {{ price | currency }}
          </span>
        </div>
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import { cancelAxiosRequest } from '@/utils/apiUtils'
import CommonApi from '@/api/commonApi'
import FormSubmitter from '@/api/formSubmitter'
import fa from '@/components/global/icon/fontAwesome'
import radioInput from '@/components/frontoffice/form/baseInput/radio'
import numberInput from '@/components/frontoffice/form/baseInput/number'
import textInput from '@/components/frontoffice/form/baseInput/text.vue'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import { mask } from 'vue-the-mask'
const api = new CommonApi()

export default {
  directives: { mask },
  components: {
    fa,
    radioInput,
    numberInput,
    textInput
  },

  data () {
    return {
      id: null,
      companyId: null,
      mode: {
        radioOptions: [
          { value: '0', label: 'travelPolicy.mode.0', selected: false },
          { value: '10', label: 'travelPolicy.mode.10', selected: false },
          { value: '20', label: 'travelPolicy.mode.20', selected: false },
          { value: '30', label: 'travelPolicy.mode.30', selected: false }
        ],
        value: null
      },
      defaultPrices: {
        room: null,
        evening: null,
        meal: null
      },
      rows: [],
      priceLabels: [
        { key: 'room', value: null },
        { key: 'evening', value: null },
        { key: 'meal', value: null },
        { key: 'no_breakfast', value: null }
      ],
      datesLabels: [
        { key: 'start', value: null },
        { key: 'end', value: null }
      ],
      add: {
        radioOptions: [
          { value: 'region', label: 'sector.type.region', selected: true },
          { value: 'department', label: 'sector.type.department', selected: false },
          { value: 'city', label: 'sector.type.city', selected: false },
          { value: 'zone', label: 'sector.type.zone', selected: false }
        ],
        selectOptions: {
          zone: [],
          city: [],
          department: [],
          region: []
        },
        values: {
          type: '',
          sector: {},
          prices: {
            room: null,
            evening: null,
            meal: null,
            no_breakfast: null
          },
          dates: {
            start: null,
            end: null
          }
        }
      },
      isLoaded: false,
      isFetching: false,
      alert: {
        def: {
          show: false,
          variant: 'success'
        },
        add: {
          show: false,
          variant: 'success'
        }
      },
      errors: []
    }
  },

  created () {
    this.companyId = this.$route.params.id

    // Import add select options
    Object.keys(this.add.selectOptions).forEach(type => {
      if (type === 'city') return
      api.getTravelPolicySelectData(type).then(r => { this.add.selectOptions[type] = r.data.data })
    })

    // Define selected add type
    this.add.radioOptions.forEach(opt => {
      if (opt.selected) this.add.values.type = opt.value
    })

    // Import mode and rows
    api.getTravelPolicy(this.companyId)
      .then(r => {
        let data = r.data.data
        let tp = data.travelPolicy

        if (tp) {
          this.id = tp.id
          let defaultRow = tp.default_travel_policy_row
          this.defaultPrices.room = defaultRow.room_price
          this.defaultPrices.evening = defaultRow.evening_price
          this.defaultPrices.meal = defaultRow.meal_price
          this.defaultPrices.no_breakfast = defaultRow.no_breakfast_price

          this.mode.radioOptions.forEach(opt => {
            if (opt.value == tp.travel_policy_mode) {
              this.mode.value = tp.travel_policy_mode
              opt.selected = true
            }
          })
        }

        this.rows = data.rows
      }).then(r => { this.isLoaded = true })
  },

  methods: {
    initAddValues () {
      this.add.values = {
        type: '',
        sector: {},
        prices: {
          room: null,
          evening: null,
          meal: null,
          no_breakfast: null
        },
        dates: {
          start: null,
          end: null
        }
      }

      // Propagate init prices
      Object.keys(this.add.values.prices).forEach(key => {
        this.$refs[key][0].$data.val = null
      })
    },

    dateTrans(key) {
      return this.$t(`travelPolicy.dates.${key}`)
    },

    priceTrans (key) {
      return this.$t(`travelPolicy.price.${key}`)
    },

    searchCity (search) {
      if (this.add.values.type !== 'city') return
      // min 2 characters
      if (search.length < 2) return
      cancelAxiosRequest()
      this.add.selectOptions.city = []
      this.isFetching = true

      api.searchCityOrPostCode(search)
        .then(resp => {
          this.add.selectOptions.city = resp.data.data
          this.test = resp.data.data
        })
        .then(() => this.isFetching = false)
    },

    onSubmitTravelPolicy () {
      let submitter = new FormSubmitter('travel_policy')
      submitter.submitRequest(
        this.companyId,
        {
          id: this.id,
          mode: this.mode.value,
          defaultPrices: {
            'room': this.defaultPrices.room || null,
            'evening': this.defaultPrices.evening || null,
            'meal': this.defaultPrices.meal || null,
            'no_breakfast': this.defaultPrices.no_breakfast || null
          }
        }
      )
        .then(r => {
          this.alert.def.show = true
          let variant

          if (r.data.success) {
            this.id = r.data.data.id
            variant = 'success'
          } else {
            variant = 'danger'
          }

          this.alert.def.variant = variant
        })
    },

    onAdd () {
      let values = Object.assign({ companyId: this.companyId }, this.add.values)
      this.errors = []

      let submitter = new FormSubmitter('travel_policy_row_add')
      submitter.submitRequest(values)
        .then(r => {
          this.alert.add.show = true
          let variant

          if (r.data.success) {
            this.rows.push(r.data.data)
            this.initAddValues()
            variant = 'success'
          } else {
            for (let i in r.data.errors) {
              this.errors.push({
                code: r.data.errors[i]
              })
            }
            variant = 'danger'
          }

          this.alert.add.variant = variant
        })
    },

    onDelete (rowId) {
      api.deleteTravelPolicyRow(rowId).then(r => {
        if (r.data.success) {
          this.rows = this.rows.filter(row => { return row.id !== rowId })
        } else {
          // TODO
        }
      })
    }
  },

  computed: {
    defaultSubmitDisabled () {
      if (this.mode.value === undefined) return true
      if (this.mode.value === '0') return false
      return !this.defaultPrices.room
    },

    addRowEnabled () {
      if (!this.id) return false
      if (this.mode.value === '0') return false
      return !!this.defaultPrices.room
    },

    addOptions () {
      this.add.values.sector = []
      return this.add.selectOptions[this.add.values.type] || []
    },

    addSubmitDisabled () {
      let values = this.add.values

      if (!this.mode.value) return true
      if (!values.type) return true
      if (!values.sector.id) return true
      return !values.prices.room
    },

    addSelectPlaceholder () {
      return this.add.values.type == 'city'
        ? 'zone.select.placeholder'
        : '_select_option'
    }
  }
}
</script>

<style lang="scss">
  #travel-policy {
    .card {
      margin: 1rem;

      .title {
        font-size: 1.1rem;
        font-weight: bold;
        cursor: pointer;
      }
    }

    .card-body {
      padding: 0;
    }

    .card-footer {
      display: flex;
      justify-content: right;
    }
  }

  .title-dates{
    margin-top: 3%;
  }
</style>
